/**
 * Amenities page: all UI strings for the property amenities page (Amenities.vue).
 * Individual amenity category names are data-driven and excluded from translation.
 */

export const amenitiesEn = {
  amenities: {
    page: {
      title: 'Amenities',
    },
    toolbar: {
      saveButton: 'Save Amenities',
    },
    banners: {
      customAmenitiesNote: 'Please note that Custom Amenities might not map to all Rental Listing Sites.',
      importedNote: 'These are amenities that have been imported from your website(s) or property management system(s) via an integration, custom feed, or website scraper. <br/> The amenities listed here do not match any of the other amenities listed above.',
    },
    filters: {
      searchLabel: 'Search',
      selectionTypeLabel: 'Selection Type',
      selectionOptions: {
        all: 'All',
        selected: 'Selected',
        notSelected: 'Not Selected',
      },
    },
    export: {
      tooltip: 'Export Selected Amenities',
      columns: {
        clientId: 'Client Id',
        propertyId: 'Property Id',
        propertyName: 'Property Name',
        propertyAddress: 'Property Address',
        amenityName: 'Amenity Name',
        amenityCategory: 'Amenity Category',
        amenityClassification: 'Amenity Classification',
        amenityType: 'Custom/Standard',
      },
    },
    sections: {
      propertyAmenities: 'Property Amenities',
      customPropertyAmenities: 'Custom Property Amenities',
      unitAmenities: 'Unit Amenities',
      customUnitAmenities: 'Custom Unit Amenities',
      commercialAmenities: 'Commercial Amenities',
      importedAmenities: 'Imported Amenities',
      selectedCount: '{count} selected | {count} selected',
      listingScoresPropertyText: 'Include 10 or more property amenities to improve your listing',
      listingScoresUnitText: 'Include 10 or more unit amenities to improve your listing',
      iframePrefix: 'Custom',
    },
    form: {
      addCustomPlaceholder: 'Add Custom Amenity...',
      addButton: 'Add',
      duplicateTitle: 'Amenity Already Exists!',
      duplicateMessage: '"{name}" already exists under {location} amenities!',
      addSuccessMessage: 'Custom amenity added!',
    },
    delete: {
      modalTitle: 'Remove Custom Amenity',
      modalText: 'Removing this Custom Amenity will affect all properties that also have this Amenity assigned.',
      confirmButton: 'Remove',
      successMessage: 'Amenity Deleted!',
    },
    translations: {
      modalTitle: 'Translations - {buildingName}',
    },
    translationsModal: {
      amenitiesTab: 'Amenities',
      utilitiesTab: 'Utilities',
      amenitiesBanner: 'Updating translations here will affect all places on your account where Amenities are used like Syndication or Websites.',
      utilitiesBanner: 'Updating translations here will affect all places on your account where Utilities are used like Syndication or Websites.',
    },
    unsavedChanges: {
      title: 'You have unsaved changes.',
    },
  },
} as const;

export const amenitiesFr = {
  amenities: {
    page: {
      title: 'Équipements',
    },
    toolbar: {
      saveButton: 'Enregistrer les équipements',
    },
    banners: {
      customAmenitiesNote: 'Veuillez noter que les équipements personnalisés pourraient ne pas être mappés à tous les sites de listes de location.',
      importedNote: 'Ce sont des équipements qui ont été importés depuis votre ou vos sites Web ou système(s) de gestion immobilière via une intégration, un flux personnalisé ou un extracteur de site Web. <br/> Les équipements listés ici ne correspondent à aucun des autres équipements listés ci-dessus.',
    },
    filters: {
      searchLabel: 'Rechercher',
      selectionTypeLabel: 'Type de sélection',
      selectionOptions: {
        all: 'Tous',
        selected: 'Sélectionnés',
        notSelected: 'Non sélectionnés',
      },
    },
    export: {
      tooltip: 'Exporter les équipements sélectionnés',
      columns: {
        clientId: 'Id client',
        propertyId: 'Id propriété',
        propertyName: 'Nom de la propriété',
        propertyAddress: 'Adresse de la propriété',
        amenityName: 'Nom de l\'équipement',
        amenityCategory: 'Catégorie d\'équipement',
        amenityClassification: 'Classification de l\'équipement',
        amenityType: 'Personnalisé/Standard',
      },
    },
    sections: {
      propertyAmenities: 'Équipements de la propriété',
      customPropertyAmenities: 'Équipements de propriété personnalisés',
      unitAmenities: 'Équipements de l\'unité',
      customUnitAmenities: 'Équipements d\'unité personnalisés',
      commercialAmenities: 'Équipements commerciaux',
      importedAmenities: 'Équipements importés',
      selectedCount: '{count} sélectionné | {count} sélectionnés',
      listingScoresPropertyText: 'Incluez 10 équipements de propriété ou plus pour améliorer votre annonce',
      listingScoresUnitText: 'Incluez 10 équipements d\'unité ou plus pour améliorer votre annonce',
      iframePrefix: 'Personnalisé',
    },
    form: {
      addCustomPlaceholder: 'Ajouter un équipement personnalisé...',
      addButton: 'Ajouter',
      duplicateTitle: 'L\'équipement existe déjà!',
      duplicateMessage: '"{name}" existe déjà sous les équipements {location}!',
      addSuccessMessage: 'Équipement personnalisé ajouté!',
    },
    delete: {
      modalTitle: 'Supprimer l\'équipement personnalisé',
      modalText: 'La suppression de cet équipement personnalisé affectera toutes les propriétés auxquelles cet équipement est également attribué.',
      confirmButton: 'Supprimer',
      successMessage: 'Équipement supprimé!',
    },
    translations: {
      modalTitle: 'Traductions - {buildingName}',
    },
    translationsModal: {
      amenitiesTab: 'Équipements',
      utilitiesTab: 'Services publics',
      amenitiesBanner: 'La mise à jour des traductions ici affectera tous les endroits sur votre compte où les équipements sont utilisés, comme la syndication ou les sites web.',
      utilitiesBanner: 'La mise à jour des traductions ici affectera tous les endroits sur votre compte où les services publics sont utilisés, comme la syndication ou les sites web.',
    },
    unsavedChanges: {
      title: 'Vous avez des modifications non enregistrées.',
    },
  },
} as const;
